import { useState, useEffect } from 'react';
import { Alert, Button, Form, Input, Typography, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import OTPInput from 'react-otp-input';

import {
	postAuthByInn,
	postInnResetSendCode,
	postInnResetValidateCode,
} from 'store/slices/customerSlice';
import { batchAddToCart } from 'store/slices/cartSlice';
import {
	getGuestCartFromStorage,
	clearGuestCartStorage,
} from 'helpers/guestCart';
import useCustomer from 'hooks/useCustomer';

const SignIn = () => {
	const [step, setStep] = useState('login');
	const [status, setStatus] = useState({ type: '', text: '' });
	const [inn, setInn] = useState('');
	const [password, setPassword] = useState('');
	const [phoneMasked, setPhoneMasked] = useState('');
	const [method, setMethod] = useState('sms');

	const { setCustomer, customer } = useCustomer();

	const mergeGuestCart = async () => {
		const guestItems = getGuestCartFromStorage();
		if (guestItems.length > 0) {
			try {
				await batchAddToCart(
					guestItems.map(i => ({
						product_id: i.product_id,
						quantity: i.quantity,
					})),
				);
				clearGuestCartStorage();
			} catch (e) {
				console.error('Merge guest cart failed', e);
			}
		}
	};

	const onLoginSuccess = async (res) => {
		setCustomer({ ...res.customer_info, store_id: customer.store_id || 0 });
		await mergeGuestCart();
		window.location.reload();
	};

	const handleLogin = async () => {
		if (inn.length < 10) return;
		setStatus({ type: 'info', text: 'Вход...' });
		try {
			const res = await postAuthByInn(inn, password);
			if (res && res.status === 'success') {
				await onLoginSuccess(res);
			} else {
				setStatus({ type: 'error', text: res?.text || 'Ошибка входа.' });
			}
		} catch {
			setStatus({ type: 'error', text: 'Ошибка соединения. Попробуйте позже.' });
		}
	};

	const handleForgotPassword = () => {
		setStep('reset-inn');
		setStatus({ type: '', text: '' });
	};

	const handleBackToLogin = () => {
		setStep('login');
		setStatus({ type: '', text: '' });
	};

	const handleSendResetCode = async () => {
		if (inn.length < 10) return;
		setStatus({ type: 'info', text: 'Отправка кода...' });
		try {
			const res = await postInnResetSendCode(inn, method);
			if (res && res.status === 'success') {
				setPhoneMasked(res.phone_masked || '***');
				setStep('reset-code');
				setStatus({ type: '', text: '' });
			} else {
				setStatus({ type: 'error', text: res?.text || 'Ошибка.' });
			}
		} catch {
			setStatus({ type: 'error', text: 'Ошибка соединения.' });
		}
	};

	const handleResendCode = async () => {
		setStatus({ type: 'info', text: 'Повторная отправка...' });
		try {
			const res = await postInnResetSendCode(inn, method);
			if (res && res.status === 'success') {
				setStatus({ type: 'success', text: 'Код отправлен повторно.' });
			} else {
				setStatus({ type: 'error', text: res?.text || 'Ошибка.' });
			}
		} catch {
			setStatus({ type: 'error', text: 'Ошибка соединения.' });
		}
	};

	if (step === 'login') {
		return (
			<LoginForm
				inn={inn}
				setInn={setInn}
				password={password}
				setPassword={setPassword}
				status={status}
				onSubmit={handleLogin}
				onForgot={handleForgotPassword}
			/>
		);
	}

	if (step === 'register') {
		return <RegisterForm onBack={handleBackToLogin} />;
	}

	if (step === 'reset-inn') {
		return (
			<ResetInnForm
				inn={inn}
				setInn={setInn}
				method={method}
				setMethod={setMethod}
				status={status}
				onSubmit={handleSendResetCode}
				onBack={handleBackToLogin}
			/>
		);
	}

	if (step === 'reset-code') {
		return (
			<ResetCodeForm
				inn={inn}
				phoneMasked={phoneMasked}
				method={method}
				status={status}
				setStatus={setStatus}
				onLoginSuccess={onLoginSuccess}
				onResend={handleResendCode}
				onBack={handleBackToLogin}
			/>
		);
	}

	return null;
};

/* ----------  Форма входа (ИНН + пароль)  ---------- */

const LoginForm = ({
	inn,
	setInn,
	password,
	setPassword,
	status,
	onSubmit,
	onForgot,
}) => (
	<Form name='signin-inn' layout='vertical' onFinish={onSubmit}>
		<Typography.Title level={2} style={{ marginBottom: 8 }}>
			Вход для контрагентов
			<Typography.Title
				level={5}
				className='text-gray'
				style={{ marginTop: 8, marginBottom: 0 }}
			>
				Введите ИНН и пароль
			</Typography.Title>
		</Typography.Title>

		<Form.Item label='ИНН' style={{ marginBottom: 16 }}>
			<Input
				value={inn}
				onChange={e => setInn(e.target.value.replace(/\D/g, '').slice(0, 12))}
				placeholder='10 или 12 цифр'
				maxLength={12}
				disabled={status?.type === 'info'}
			/>
		</Form.Item>

		<Form.Item label='Пароль' style={{ marginBottom: 8 }}>
			<Input.Password
				value={password}
				onChange={e => setPassword(e.target.value)}
				disabled={status?.type === 'info'}
			/>
		</Form.Item>

		<div style={{ textAlign: 'right', marginBottom: 16 }}>
			<Button type='link' onClick={onForgot} style={{ padding: 0 }}>
				Забыли пароль?
			</Button>
		</div>

		<Form.Item style={{ marginBottom: 12 }}>
			<Button
				type='primary'
				htmlType='submit'
				block
				size='large'
				disabled={inn.length < 10 || !password}
				loading={status?.type === 'info'}
			>
				Войти
			</Button>
		</Form.Item>

		{status?.type && status.type !== 'info' && (
			<Alert message={status.text} type={status.type} banner />
		)}
	</Form>
);

const stripLeadingOoo = value =>
	value
		.replace(/^\s*(ООО|ooo|Ooo|ооо)\s*[«"']?\s*/i, '')
		.replace(/^[«"']+/, '')
		.trimStart();

/* ----------  Заявка на аккаунт  ---------- */

const RegisterForm = ({ onBack }) => {
	const [inn, setInn] = useState('');
	const [kpp, setKpp] = useState('');
	const [legalAddress, setLegalAddress] = useState('');
	const [companyName, setCompanyName] = useState('');
	const [fileList, setFileList] = useState([]);
	const [submitted, setSubmitted] = useState(false);

	const canSubmit =
		(inn.length === 10 || inn.length === 12) &&
		kpp.length === 9 &&
		legalAddress.trim().length > 5 &&
		companyName.trim().length > 1 &&
		fileList.length > 0;

	const handleSubmit = () => {
		if (!canSubmit) return;
		setSubmitted(true);
	};

	if (submitted) {
		return (
			<div>
				<Typography.Title level={2} style={{ marginBottom: 8 }}>
					Заявка отправлена
					<Typography.Title
						level={5}
						className='text-gray'
						style={{ marginTop: 8, marginBottom: 0 }}
					>
						Дождитесь одобрения. После проверки с вами свяжутся.
					</Typography.Title>
				</Typography.Title>
				<Button type='link' onClick={onBack} style={{ padding: 0, marginTop: 16 }}>
					Назад ко входу
				</Button>
			</div>
		);
	}

	return (
		<Form name='register-contractor' layout='vertical' onFinish={handleSubmit}>
			<Typography.Title level={2} style={{ marginBottom: 8 }}>
				Создать аккаунт
				<Typography.Title
					level={5}
					className='text-gray'
					style={{ marginTop: 8, marginBottom: 0 }}
				>
					Заявка на доступ для контрагента. После проверки аккаунт одобрят.
				</Typography.Title>
			</Typography.Title>

			<Form.Item label='ИНН' style={{ marginBottom: 16 }} required>
				<Input
					value={inn}
					onChange={e => setInn(e.target.value.replace(/\D/g, '').slice(0, 12))}
					placeholder='10 или 12 цифр'
					maxLength={12}
				/>
			</Form.Item>

			<Form.Item label='КПП' style={{ marginBottom: 16 }} required>
				<Input
					value={kpp}
					onChange={e => setKpp(e.target.value.replace(/\D/g, '').slice(0, 9))}
					placeholder='9 цифр'
					maxLength={9}
				/>
			</Form.Item>

			<Form.Item
				label='Наименование'
				style={{ marginBottom: 16 }}
				required
				extra='Рабочее название без «ООО» в начале'
			>
				<Input
					value={companyName}
					onChange={e => setCompanyName(stripLeadingOoo(e.target.value))}
					placeholder='Нальчикский мясокомбинат'
				/>
			</Form.Item>

			<Form.Item label='Юридический адрес' style={{ marginBottom: 16 }} required>
				<Input.TextArea
					value={legalAddress}
					onChange={e => setLegalAddress(e.target.value)}
					placeholder='Индекс, регион, город, улица, дом'
					rows={3}
				/>
			</Form.Item>

			<Form.Item
				label='Документы'
				style={{ marginBottom: 16 }}
				required
				extra='Сканы ИНН, устава и пр. PDF, JPG, PNG, DOC — до 10 файлов'
			>
				<Upload
					multiple
					fileList={fileList}
					beforeUpload={() => false}
					onChange={({ fileList: next }) => setFileList(next.slice(0, 10))}
					accept='.pdf,.jpg,.jpeg,.png,.webp,.doc,.docx'
				>
					<Button icon={<UploadOutlined />}>Прикрепить файлы</Button>
				</Upload>
			</Form.Item>

			<Form.Item style={{ marginBottom: 12 }}>
				<Button
					type='primary'
					htmlType='submit'
					block
					size='large'
					disabled={!canSubmit}
				>
					Отправить заявку
				</Button>
			</Form.Item>

			<Button type='link' onClick={onBack} style={{ padding: 0 }}>
				Назад ко входу
			</Button>
		</Form>
	);
};

/* ----------  Шаг 1 сброса: ввод ИНН  ---------- */

const ResetInnForm = ({ inn, setInn, method, setMethod, status, onSubmit, onBack }) => (
	<Form name='reset-inn' layout='vertical' onFinish={onSubmit}>
		<Typography.Title level={2} style={{ marginBottom: 8 }}>
			Сброс пароля
			<Typography.Title
				level={5}
				className='text-gray'
				style={{ marginTop: 8, marginBottom: 0 }}
			>
				На телефон, привязанный к ИНН, придёт код подтверждения
			</Typography.Title>
		</Typography.Title>

		<Form.Item label='ИНН' style={{ marginBottom: 16 }}>
			<Input
				value={inn}
				onChange={e => setInn(e.target.value.replace(/\D/g, '').slice(0, 12))}
				placeholder='10 или 12 цифр'
				maxLength={12}
				disabled={status?.type === 'info'}
			/>
		</Form.Item>

		<Form.Item label='Как отправить код?' style={{ marginBottom: 16 }}>
			<Button.Group style={{ width: '100%' }}>
				<Button
					type={method === 'sms' ? 'primary' : 'default'}
					onClick={() => setMethod('sms')}
					disabled={status?.type === 'info'}
					style={{ width: '50%' }}
				>
					SMS
				</Button>
				<Button
					type={method === 'whatsapp' ? 'primary' : 'default'}
					onClick={() => setMethod('whatsapp')}
					disabled={status?.type === 'info'}
					style={{ width: '50%' }}
				>
					WhatsApp
				</Button>
			</Button.Group>
		</Form.Item>

		<Form.Item style={{ marginBottom: 12 }}>
			<Button
				type='primary'
				htmlType='submit'
				block
				size='large'
				disabled={inn.length < 10}
				loading={status?.type === 'info'}
			>
				Отправить код
			</Button>
		</Form.Item>

		<Button type='link' onClick={onBack} style={{ padding: 0 }}>
			Назад ко входу
		</Button>

		{status?.type && status.type !== 'info' && (
			<Alert message={status.text} type={status.type} banner style={{ marginTop: 12 }} />
		)}
	</Form>
);

/* ----------  Шаг 2 сброса: код + новый пароль  ---------- */

const ResetCodeForm = ({ inn, phoneMasked, method, status, setStatus, onLoginSuccess, onResend, onBack }) => {
	const [code, setCode] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [newPasswordRepeat, setNewPasswordRepeat] = useState('');
	const [loading, setLoading] = useState(false);

	function fmtMSS(s) {
		return (s - (s %= 60)) / 60 + (9 < s ? ':' : ':0') + s;
	}

	const [timer, setTimer] = useState(120);
	useEffect(() => {
		const interval = setInterval(() => {
			if (timer > 0) setTimer(t => t - 1);
		}, 1000);
		return () => clearInterval(interval);
	}, [timer]);

	const handleResend = async () => {
		setTimer(120);
		await onResend();
	};

	const handleSubmit = async () => {
		if (newPassword !== newPasswordRepeat) {
			setStatus({ type: 'error', text: 'Пароли не совпадают.' });
			return;
		}
		setLoading(true);
		setStatus({ type: 'info', text: 'Установка пароля...' });
		try {
			const res = await postInnResetValidateCode(inn, code, newPassword);
			if (res && res.status === 'success') {
				await onLoginSuccess(res);
			} else {
				setStatus({ type: 'error', text: res?.text || 'Ошибка.' });
			}
		} catch {
			setStatus({ type: 'error', text: 'Ошибка соединения.' });
		}
		setLoading(false);
	};

	return (
		<Form name='reset-code' layout='vertical' onFinish={handleSubmit}>
			<Typography.Title level={2} style={{ marginBottom: 8 }}>
				Новый пароль
				<Typography.Title
					level={5}
					className='text-gray'
					style={{ marginTop: 8, marginBottom: 0 }}
				>
					Код отправлен {method === 'whatsapp' ? 'в WhatsApp' : 'по SMS'} на {phoneMasked}
				</Typography.Title>
			</Typography.Title>

			<Form.Item label='Код из SMS' style={{ marginBottom: 16 }}>
				<OTPInput
					value={code}
					onChange={setCode}
					numInputs={4}
					inputType='number'
					shouldAutoFocus
					renderSeparator={<span className='ml-10 mr-10'>-</span>}
					renderInput={props => (
						<Input {...props} className='rn-otp-input' disabled={loading} />
					)}
				/>
			</Form.Item>

			<Form.Item label='Новый пароль' style={{ marginBottom: 16 }}>
				<Input.Password
					value={newPassword}
					onChange={e => setNewPassword(e.target.value)}
					disabled={loading}
				/>
			</Form.Item>

			<Form.Item label='Повторите пароль' style={{ marginBottom: 16 }}>
				<Input.Password
					value={newPasswordRepeat}
					onChange={e => setNewPasswordRepeat(e.target.value)}
					disabled={loading}
				/>
			</Form.Item>

			<Form.Item style={{ marginBottom: 12 }}>
				<Button
					type='primary'
					htmlType='submit'
					block
					size='large'
					disabled={code.length < 4 || newPassword.length < 4 || !newPasswordRepeat}
					loading={loading}
				>
					Установить пароль и войти
				</Button>
			</Form.Item>

			<p>
				{timer === 0 ? (
					<Button type='link' onClick={handleResend} style={{ padding: 0 }}>
						Отправить код повторно
					</Button>
				) : (
					<span style={{ color: '#999' }}>
						Отправить код повторно через {fmtMSS(timer)}
					</span>
				)}
			</p>

			<Button type='link' onClick={onBack} style={{ padding: 0 }}>
				Назад ко входу
			</Button>

			{status?.type && status.type !== 'info' && (
				<Alert message={status.text} type={status.type} banner style={{ marginTop: 12 }} />
			)}
		</Form>
	);
};

export default SignIn;
